import { llpBtbFindingSchema, llpBtbPartResultSchema, llpBtbPushedCheckSchema, llpBtbRuleIdSchema, llpBtbVersionSchema, } from './llp-btb-ready.webhook.js';
import z from 'zod';
/** One row of a tracked part set composition (`LlpBtbSetRow`, DR-5467). */
const setRowShape = {
    serialNumber: z.string(),
    partNumber: z.string().optional(),
    position: z.string().optional(),
    status: z.string().optional(),
    tsn: z.number().optional(),
    csn: z.number().optional(),
};
const setRowSchema = z.looseObject(setRowShape);
const setMemberSchema = z.looseObject({
    ...setRowShape,
    disposition: z.enum(['new', 'replaced', 'repaired', 'unknown']),
    /** The paired OFF row's serial, as printed: present only with `disposition: 'replaced'` (DR-5569). */
    replacedSerialNumber: z.string().optional(),
    fittedAt: z.string().optional(),
});
const setCompositionSchema = z.looseObject({
    documentId: z.string(),
    pageIds: z.array(z.string()),
    category: z.string(),
    date: z.string().optional(),
    esn: z.string(),
    engineTsn: z.number().optional(),
    engineCsn: z.number().optional(),
    sheetTitle: z.string().optional(),
    workOrder: z.string().optional(),
    setSerialNumber: z.string().optional(),
    kind: z.enum(['off-on', 'list']),
    members: z.array(setMemberSchema),
    removed: z.array(setRowSchema),
    hostPartKey: z.string().optional(),
    hostMissing: z.literal(true).optional(),
});
const setSchema = z.looseObject({
    setKey: z.string(),
    partNumber: z.string(),
    serialNumber: z.string().optional(),
    name: z.string().optional(),
    partKind: z.enum(['hpt-blade', 'hpt-nozzle', 'lpt-nozzle', 'fan-blade', 'other']),
    stage: z.number().optional(),
    hostPartKey: z.string().optional(),
    members: z.array(setMemberSchema),
    compositions: z.array(setCompositionSchema),
    fitEvent: z
        .looseObject({
        compositionIndex: z.number().int().nonnegative(),
        date: z.string().optional(),
        documentId: z.string(),
    })
        .optional(),
    result: llpBtbPartResultSchema,
    checks: z.array(llpBtbPushedCheckSchema),
    findingIds: z.array(z.string()),
});
/**
 * The tracked part sets of the working engine (DR-5519), one envelope per project: the set rows of
 * the stored LLP back-to-birth trace (DR-5467, the HPT blade set of each stage), their T rules and
 * their findings. They left `llp-btb-ready`, which carries LLP rows only, so its consumer can keep
 * a required serial. A set's own `serialNumber` is optional and additive (DR-5538): the assembly
 * serial a sheet header prints (a GE EMU Serial No on HPT / LPT nozzle Tracked Parts Lists),
 * absent on HPT blade and fan blade sets and when no sheet prints one.
 *
 * Versioned like `llp-btb-ready` (the same stored trace): `{ projectId, version.generation,
 * version.rulesVersion }` plus the persisted finalize timestamp `generatedAt`, so retries are byte-identical and
 * a consumer orders envelopes by `generation`. `rulesEvaluated` lists the T rules the trace
 * evaluated (empty when it holds no set). `findings[]` uses the `llp-btb-ready` finding shape and
 * holds the findings linked to a set; a set lists its findings in `findingIds`.
 *
 * Neither a timeline nor a check's `evidence` is pushed, and nothing serves them for a set yet (the
 * pull route serves LLP rows only). The schema is loose like every family's, so the payload builder
 * picks the set fields itself. There is no omitted shape: a push over the ceiling is not sent and
 * the family stays owed.
 *
 * A retraction is a push with `sets` and `findings` empty: the project has no tracked part set any
 * more, so the consumer clears the sets it holds.
 */
export const trackedPartSetReadyWebhookSchema = z.looseObject({
    type: z.literal('tracked-part-set-ready'),
    projectId: z.string(),
    generatedAt: z.iso.datetime(),
    esn: z.string(),
    version: llpBtbVersionSchema,
    rulesEvaluated: z.array(llpBtbRuleIdSchema),
    sets: z.array(setSchema),
    findings: z.array(llpBtbFindingSchema),
});
//# sourceMappingURL=tracked-part-set-ready.webhook.js.map